import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Trash2, RotateCcw, Clock, X, MessageCircle, MessageSquare, FileText, Camera, Bell, Ticket, Users, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const KIND_ICON = {
  conversation:   MessageCircle,
  dm:             MessageCircle,
  comment:        MessageSquare,
  community_post: FileText,
  story:          Camera,
  notification:   Bell,
  ticket:         Ticket,
  ticket_msg:     Ticket,
  staff_msg:      Users,
  product:        ShoppingBag,
};

const KIND_FILTERS = [
  { key: "",              label: "All" },
  { key: "conversation",  label: "Conversations" },
  { key: "dm",            label: "DMs" },
  { key: "comment",       label: "Comments" },
  { key: "community_post",label: "Posts" },
  { key: "story",         label: "Stories" },
  { key: "notification",  label: "Notifications" },
  { key: "ticket",        label: "Tickets" },
  { key: "ticket_msg",    label: "Ticket replies" },
  { key: "staff_msg",     label: "Staff chat" },
];

function timeUntilPurge(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "purging…";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function snapshotPreview(item) {
  const s = item.snapshot || {};
  if (item.kind === "conversation") {
    return s.last_message || "Empty conversation";
  }
  if (item.kind === "dm" || item.kind === "staff_msg" || item.kind === "ticket_msg") {
    return s.text || s.body || "(empty message)";
  }
  if (item.kind === "comment") {
    return s.text || "(empty comment)";
  }
  if (item.kind === "community_post") {
    return s.caption || "(no caption)";
  }
  if (item.kind === "notification") {
    return `${s.title || ""}${s.body ? ` — ${s.body}` : ""}`;
  }
  if (item.kind === "ticket") {
    return s.subject || s.category || "(ticket)";
  }
  if (item.kind === "story") {
    return s.caption || "Story media";
  }
  return JSON.stringify(s).slice(0, 140);
}

export default function TrashBin() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {action:"restore"|"purge", item}

  const load = useCallback(async (kind) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/me/trash", { params: kind ? { kind } : {} });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load trash");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const doRestore = async (item) => {
    setBusyId(item.trash_id);
    try {
      await api.post(`/me/trash/${item.trash_id}/restore`);
      toast.success("Restored");
      await load(filter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't restore");
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const doPurge = async (item) => {
    setBusyId(item.trash_id);
    try {
      await api.delete(`/me/trash/${item.trash_id}`);
      toast.success("Permanently deleted");
      await load(filter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't delete");
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const totalCount = useMemo(
    () => Object.values(counts).reduce((s, n) => s + n, 0),
    [counts]
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5 pb-24" data-testid="trash-page">
      <button
        onClick={() => navigate("/profile")}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="trash-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to profile
      </button>

      <header>
        <div className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-widest font-black">
          <Trash2 className="w-3 h-3" /> Personal Trash
        </div>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Trash bin</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-lg">
          Anything you delete lands here for <span className="text-pink-300 font-bold">24 hours</span>.
          Restore items you didn't mean to delete, or wipe them forever.
        </p>
        <p className="text-xs text-zinc-500 mt-2">
          <span className="text-white font-bold" data-testid="trash-total">{totalCount}</span> item{totalCount === 1 ? "" : "s"} pending
        </p>
      </header>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="trash-filter-chips">
        {KIND_FILTERS.map((f) => {
          const n = f.key ? counts[f.key] || 0 : totalCount;
          const active = filter === f.key;
          return (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`trash-chip-${f.key || "all"}`}
            >
              {f.label}
              <span className="text-[10px] text-zinc-400 font-bold">{n}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center text-zinc-500 py-10">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="trash-empty">
          <Trash2 className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
          <p className="text-sm text-zinc-400">Your trash bin is empty.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="trash-list">
          {items.map((item) => (
            <div
              key={item.trash_id}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-3"
              data-testid={`trash-row-${item.trash_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-zinc-900 border border-white/10 grid place-items-center shrink-0 text-pink-300">
                  {(() => {
                    const Icon = KIND_ICON[item.kind] || Trash2;
                    return <Icon className="w-4 h-4" />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[10px] uppercase tracking-widest font-black text-pink-300">
                      {item.label}
                    </p>
                    <p className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Purges in <span className="text-zinc-300 font-bold">{timeUntilPurge(item.purge_at)}</span>
                    </p>
                  </div>
                  <p className="text-sm text-zinc-100 mt-1 line-clamp-2" data-testid={`trash-preview-${item.trash_id}`}>
                    {snapshotPreview(item)}
                  </p>
                  <div className="mt-2.5 flex gap-1.5 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => setConfirm({ action: "restore", item })}
                      disabled={busyId === item.trash_id}
                      className="h-7 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-200 border border-cyan-400/30"
                      data-testid={`trash-restore-${item.trash_id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" /> Restore
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setConfirm({ action: "purge", item })}
                      disabled={busyId === item.trash_id}
                      className="h-7 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-400/30"
                      data-testid={`trash-purge-${item.trash_id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Delete forever
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="trash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "restore" ? "Restore this item?" : "Delete forever?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "restore"
                ? "It will reappear wherever it used to live."
                : "This cannot be undone — the item is wiped from your account permanently."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirm?.action === "restore"
                  ? doRestore(confirm.item)
                  : doPurge(confirm.item)
              }
              className={confirm?.action === "purge" ? "bg-red-500 hover:bg-red-400" : ""}
              data-testid="trash-confirm-action"
            >
              {confirm?.action === "restore" ? "Restore" : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
